// src/redux/coach/coachSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  addCoachAPI,
  updateCoachAPI,
  getCoachListAPI,
  getCoachDetailAPI,
  getCoachListAllAPI,
  assignCoachAPI,
  deleteCoachAPI,
  getAssociatedCompaniesAPI,
  getCoachCompaniesAPI,
  getCoachCompaniesByMonthAPI, // ✅ NEW
} from "./coachApi";

// =================== THUNKS ===================

// ✅ Add Coach
export const addCoach = createAsyncThunk(
  "coach/addCoach",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await addCoachAPI(formData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to add coach");
    }
  }
);

// ✅ Update Coach
export const updateCoach = createAsyncThunk(
  "coach/updateCoach",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await updateCoachAPI({ id, formData });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update coach");
    }
  }
);

// ✅ Get Coach List Paginated
export const getCoachList = createAsyncThunk(
  "coach/getCoachList",
  async ({ page = 1, limit = 10, search = "" } = {}, { rejectWithValue }) => {
    try {
      const response = await getCoachListAPI({ page, limit, search });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch coach list");
    }
  }
);

// ✅ Get Coach Detail
export const getCoachById = createAsyncThunk(
  "coach/getCoachDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getCoachDetailAPI(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch coach details");
    }
  }
);

// ✅ Get All Coaches (No Pagination) with excludeCompany
export const getCoachListAll = createAsyncThunk(
  "coach/getCoachListAll",
  async ({ excludeCompany } = {}, { rejectWithValue }) => {
    try {
      const response = await getCoachListAllAPI({ excludeCompany });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch all coaches");
    }
  }
);

// ✅ Assign Coaches to Company
export const assignCoach = createAsyncThunk(
  "coach/assignCoach",
  async ({ companyId, coachIds }, { rejectWithValue }) => {
    try {
      const response = await assignCoachAPI({ companyId, coachIds });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to assign coaches");
    }
  }
);

// ✅ Delete Coach
export const deleteCoach = createAsyncThunk(
  "coach/deleteCoach",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteCoachAPI(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete coach");
    }
  }
);

// ✅ Get Associated Companies (No params needed)
export const getAssociatedCompanies = createAsyncThunk(
  "coach/getAssociatedCompanies",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAssociatedCompaniesAPI();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch associated companies");
    }
  }
);

// ✅ Get Companies for a particular coach (paginated)
export const getCoachCompanies = createAsyncThunk(
  "coach/getCoachCompanies",
  async ({ coachId, page = 1, limit = 10, search = "" } = {}, { rejectWithValue }) => {
    try {
      const response = await getCoachCompaniesAPI({ coachId, page, limit, search });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch coach companies");
    }
  }
);

// ✅ NEW: Get Companies by Month for a Coach
export const getCoachCompaniesByMonth = createAsyncThunk(
  "coach/getCoachCompaniesByMonth",
  async (coachId, { rejectWithValue }) => {
    try {
      const response = await getCoachCompaniesByMonthAPI(coachId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch companies by month");
    }
  }
);

// =================== INITIAL STATE ===================
const initialState = {
  addCoach: { data: null, loading: false, error: null },
  updateCoach: { data: null, loading: false, error: null },
  getCoachList: { data: [], total: 0, loading: false, error: null },
  getCoachListAll: { data: [], loading: false, error: null },
  getCoachById: { data: null, loading: false, error: null },
  assignCoach: { data: null, loading: false, error: null },
  deleteCoach: { data: null, loading: false, error: null },
  getAssociatedCompanies: { data: [], loading: false, error: null },
  getCoachCompanies: { data: [], total: 0, loading: false, error: null },
  // ✅ NEW
  getCoachCompaniesByMonth: { data: null, loading: false, error: null },
};

// =================== SLICE ===================
const coachSlice = createSlice({
  name: "coach",
  initialState,
  reducers: {
    clearAddCoach: (state) => {
      state.addCoach.data = null;
      state.addCoach.error = null;
    },
    clearUpdateCoach: (state) => {
      state.updateCoach.data = null;
      state.updateCoach.error = null;
    },
    clearCoachList: (state) => {
      state.getCoachList.data = [];
      state.getCoachList.error = null;
    },
    clearCoachDetail: (state) => {
      state.getCoachById.data = null;
      state.getCoachById.error = null;
    },
    clearCoachListAll: (state) => {
      state.getCoachListAll.data = [];
      state.getCoachListAll.error = null;
    },
    clearAssignCoach: (state) => {
      state.assignCoach.data = null;
      state.assignCoach.error = null;
    },
    clearDeleteCoach: (state) => {
      state.deleteCoach.data = null;
      state.deleteCoach.error = null;
    },
    clearAssociatedCompanies: (state) => {
      state.getAssociatedCompanies.data = [];
      state.getAssociatedCompanies.error = null;
    },
    clearCoachCompanies: (state) => {
      state.getCoachCompanies.data = [];
      state.getCoachCompanies.total = 0;
      state.getCoachCompanies.error = null;
    },
    // ✅ NEW
    clearCoachCompaniesByMonth: (state) => {
      state.getCoachCompaniesByMonth.data = null;
      state.getCoachCompaniesByMonth.error = null;
    },
  },

  extraReducers: (builder) => {
    // ----- Add Coach -----
    builder
      .addCase(addCoach.pending, (state) => {
        state.addCoach.loading = true;
        state.addCoach.error = null;
      })
      .addCase(addCoach.fulfilled, (state, action) => {
        state.addCoach.loading = false;
        state.addCoach.data = action.payload?.data || action.payload;
      })
      .addCase(addCoach.rejected, (state, action) => {
        state.addCoach.loading = false;
        state.addCoach.error = action.payload;
      });

    // ----- Update Coach -----
    builder
      .addCase(updateCoach.pending, (state) => {
        state.updateCoach.loading = true;
        state.updateCoach.error = null;
      })
      .addCase(updateCoach.fulfilled, (state, action) => {
        state.updateCoach.loading = false;
        state.updateCoach.data = action.payload?.data || action.payload;
      })
      .addCase(updateCoach.rejected, (state, action) => {
        state.updateCoach.loading = false;
        state.updateCoach.error = action.payload;
      });

    // ----- Coach List (Paginated) -----
    builder
      .addCase(getCoachList.pending, (state) => {
        state.getCoachList.loading = true;
        state.getCoachList.error = null;
      })
      .addCase(getCoachList.fulfilled, (state, action) => {
        state.getCoachList.loading = false;
        state.getCoachList.data = action.payload?.data || [];
        state.getCoachList.total = action.payload?.total || 0;
      })
      .addCase(getCoachList.rejected, (state, action) => {
        state.getCoachList.loading = false;
        state.getCoachList.error = action.payload;
      });

    // ----- Coach Detail -----
    builder
      .addCase(getCoachById.pending, (state) => {
        state.getCoachById.loading = true;
        state.getCoachById.error = null;
      })
      .addCase(getCoachById.fulfilled, (state, action) => {
        state.getCoachById.loading = false;
        state.getCoachById.data = action.payload?.data || action.payload;
      })
      .addCase(getCoachById.rejected, (state, action) => {
        state.getCoachById.loading = false;
        state.getCoachById.error = action.payload;
      });

    // ----- Coach List All (No Pagination) -----
    builder
      .addCase(getCoachListAll.pending, (state) => {
        state.getCoachListAll.loading = true;
        state.getCoachListAll.error = null;
      })
      .addCase(getCoachListAll.fulfilled, (state, action) => {
        state.getCoachListAll.loading = false;
        state.getCoachListAll.data = action.payload?.data || [];
      })
      .addCase(getCoachListAll.rejected, (state, action) => {
        state.getCoachListAll.loading = false;
        state.getCoachListAll.error = action.payload;
      });

    // ----- Assign Coach -----
    builder
      .addCase(assignCoach.pending, (state) => {
        state.assignCoach.loading = true;
        state.assignCoach.error = null;
      })
      .addCase(assignCoach.fulfilled, (state, action) => {
        state.assignCoach.loading = false;
        state.assignCoach.data = action.payload?.data || action.payload;
      })
      .addCase(assignCoach.rejected, (state, action) => {
        state.assignCoach.loading = false;
        state.assignCoach.error = action.payload;
      });

    // ----- Delete Coach -----
    builder
      .addCase(deleteCoach.pending, (state) => {
        state.deleteCoach.loading = true;
        state.deleteCoach.error = null;
      })
      .addCase(deleteCoach.fulfilled, (state, action) => {
        state.deleteCoach.loading = false;
        state.deleteCoach.data = action.payload?.data || action.payload;
      })
      .addCase(deleteCoach.rejected, (state, action) => {
        state.deleteCoach.loading = false;
        state.deleteCoach.error = action.payload;
      });

    // ----- Associated Companies -----
    builder
      .addCase(getAssociatedCompanies.pending, (state) => {
        state.getAssociatedCompanies.loading = true;
        state.getAssociatedCompanies.error = null;
      })
      .addCase(getAssociatedCompanies.fulfilled, (state, action) => {
        state.getAssociatedCompanies.loading = false;
        state.getAssociatedCompanies.data = action.payload?.data || [];
      })
      .addCase(getAssociatedCompanies.rejected, (state, action) => {
        state.getAssociatedCompanies.loading = false;
        state.getAssociatedCompanies.error = action.payload;
      });

    // ----- Get Companies for a Coach -----
    builder
      .addCase(getCoachCompanies.pending, (state) => {
        state.getCoachCompanies.loading = true;
        state.getCoachCompanies.error = null;
      })
      .addCase(getCoachCompanies.fulfilled, (state, action) => {
        state.getCoachCompanies.loading = false;
        state.getCoachCompanies.data = action.payload?.data || [];
        state.getCoachCompanies.total =
          action.payload?.pagination?.total ||
          action.payload?.pagination?.totalCompanies ||
          action.payload?.total ||
          action.payload?.count ||
          0;
      })
      .addCase(getCoachCompanies.rejected, (state, action) => {
        state.getCoachCompanies.loading = false;
        state.getCoachCompanies.error = action.payload;
      });

    // ----- NEW: Get Companies by Month -----
    builder
      .addCase(getCoachCompaniesByMonth.pending, (state) => {
        state.getCoachCompaniesByMonth.loading = true;
        state.getCoachCompaniesByMonth.error = null;
      })
      .addCase(getCoachCompaniesByMonth.fulfilled, (state, action) => {
        state.getCoachCompaniesByMonth.loading = false;
        state.getCoachCompaniesByMonth.data = action.payload?.data || action.payload;
      })
      .addCase(getCoachCompaniesByMonth.rejected, (state, action) => {
        state.getCoachCompaniesByMonth.loading = false;
        state.getCoachCompaniesByMonth.error = action.payload;
      });
  },
});

export const {
  clearAddCoach,
  clearUpdateCoach,
  clearCoachList,
  clearCoachDetail,
  clearCoachListAll,
  clearAssignCoach,
  clearDeleteCoach,
  clearAssociatedCompanies,
  clearCoachCompanies,
  clearCoachCompaniesByMonth, // ✅ NEW
} = coachSlice.actions;

export default coachSlice.reducer;